import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CloseOutlined from '@material-ui/icons/CloseOutlined';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import { get, chain } from 'lodash';
import { pluralize } from 'services/helpers/formatters';
import formatPrice, { formatCentsPrice } from 'services/helpers/formatPrice';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
  },

  infoButton: {
    color: theme.palette.secondary.contrastText,
    marginLeft: theme.spacing(1.5),
  },

  content: {
    padding: theme.spacing(2),
    width: theme.spacing(40),
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  popoverHeaderContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  popoverHeader: {
    fontSize: '12px',
    fontWeight: 700,
  },

  closeButton: {
    padding: 0,
    color: theme.palette.secondary.contrastText,
  },

  amountContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  amountLabel: {
    fontSize: '12px',
    fontWeight: 700,
    color: theme.palette.secondary.contrastText,
  },

  amount: {
    fontSize: '18px',
    fontWeight: 700,
  },
}));

const LabelAmount = (props) => {
  const { record } = props;

  const labelPrice = get(record, 'details.labelPrice');
  const currency = Object.keys(get(record, 'amounts') ?? {}).find((key) => key !== 'LABEL');

  return <span>-{formatPrice(labelPrice, { currency })}</span>;
};

const AmountsField = (props) => {
  const { record, source } = props;
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const handlePopoverClick = (event) => {
    event.stopPropagation();
  };

  const partiallyAvailable =
    record.status === 'PENDING' && record.details?.achAllocation?.instantAmountCents;

  const handleAmount = (value, currency) => {
    if (currency === 'LABEL') {
      return pluralize(value, 'label');
    }

    return value.toLocaleString('en-US', { style: 'currency', currency });
  };

  const amountsObject: { [key: string]: number } = get(record, source);

  let amountContent;

  if (partiallyAvailable) {
    amountContent = (
      <div>
        {!!record.details?.achAllocation?.instantAmountCents && (
          <span>{formatCentsPrice(record.details.achAllocation.instantAmountCents)}</span>
        )}
        {!!record.details?.achAllocation?.pendingAmountCents && (
          <>
            <br />
            <span>
              {formatCentsPrice(record.details.achAllocation.pendingAmountCents)} in 2-3 business
              days
            </span>
          </>
        )}
      </div>
    );
  } else if (!amountsObject) {
    amountContent = <span>-</span>;
  } else {
    const amountsValue = chain(amountsObject)
      .map((value, currency) => ({ value, currency }))
      .sortBy(['currency.length', 'currency'])
      .map(({ value, currency }) => handleAmount(value, currency))
      .value();

    amountContent =
      record?.type === 'PURCHASE' && !Object.keys(amountsObject ?? {}).includes('LABEL') ? (
        <LabelAmount record={record} />
      ) : (
        <span>{amountsValue.join(', ')}</span>
      );
  }

  const isBankAccountDeposit =
    record.details?.description === 'Deposit - Bank Account' ||
    record.details?.description === 'Auto-refill - Bank Account';

  if (!isBankAccountDeposit) {
    return amountContent;
  }

  return (
    <div className={classes.container}>
      {amountContent}
      <IconButton
        aria-label="Bank deposit allocation details"
        className={classes.infoButton}
        size="small"
        onClick={handleOpen}>
        <InfoOutlined />
      </IconButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={handlePopoverClick}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}>
        <Box className={classes.content}>
          <Box className={classes.popoverHeaderContainer}>
            <Typography className={classes.popoverHeader}>PAYMENT PARTIALLY AVAILABLE</Typography>
            <IconButton
              aria-label="Close bank deposit allocation details"
              className={classes.closeButton}
              onClick={handleClose}>
              <CloseOutlined />
            </IconButton>
          </Box>

          <Box className={classes.amountContainer}>
            <Typography className={classes.amountLabel}>AVAILABLE NOW</Typography>
            <Typography className={classes.amount}>
              {formatCentsPrice(record.details?.achAllocation?.instantAmountCents)}
            </Typography>
          </Box>
          <Box className={classes.amountContainer}>
            <Typography className={classes.amountLabel}>AVAILABLE IN 2-3 DAYS</Typography>
            <Typography className={classes.amount}>
              {formatCentsPrice(record.details?.achAllocation?.pendingAmountCents)}
            </Typography>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

export default AmountsField;
